import { Link } from "react-router-dom";
import { ShieldCheck, ExternalLink, Youtube } from "lucide-react";
import { YOUTUBE_CHANNEL_URL } from "../../config/videoGuides";
import { DELTA_REFERRAL_URL } from "../../config/referral";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full border-t border-gray-200 dark:border-gray-800 bg-gray-50/90 dark:bg-gray-950/80 text-gray-600 dark:text-gray-400 text-xs transition-colors">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          
          {/* Brand Col */}
          <div className="md:col-span-1">
            <Link to="/" className="text-lg font-black text-blue-600 dark:text-blue-400 tracking-wide flex items-center gap-2 mb-3">
              AlgoMasters
            </Link>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
              Institutional-grade automated crypto options & derivatives execution platform. Non-custodial, high-frequency, and powered by autonomous AI sentinels.
            </p>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 text-emerald-600 dark:text-emerald-400 text-[11px] font-semibold">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>All Systems Operational</span>
            </div>
          </div>

          {/* Platform Links */}
          <div>
            <h4 className="text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-3">
              Platform
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/bots" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Autonomous AI Bots
                </Link>
              </li>
              <li>
                <Link to="/strategies" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Option Strategies
                </Link>
              </li>
              <li>
                <Link to="/brokers" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Broker Integration (Delta)
                </Link>
              </li>
              <li>
                <Link to="/reports" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Analytics & Reports
                </Link>
              </li>
              <li>
                <Link to="/system-health" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  System Health & Uptime
                </Link>
              </li>
            </ul>
          </div>

          {/* Company & Support */}
          <div>
            <h4 className="text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-3">
              Company
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Contact Support
                </Link>
              </li>
              <li>
                <a
                  href={YOUTUBE_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-red-600 dark:text-red-400 hover:text-red-500 transition-colors font-medium"
                >
                  <Youtube size={12} /> YouTube Tutorials <ExternalLink size={10} />
                </a>
              </li>
              <li>
                <a
                  href={DELTA_REFERRAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  Delta Exchange <ExternalLink size={11} />
                </a>
              </li>
              <li>
                <Link to="/login" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Sign In
                </Link>
              </li>
              <li>
                <Link to="/register" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Create Account
                </Link>
              </li>
            </ul>
          </div>

          {/* Compliance & Legal */}
          <div>
            <h4 className="text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-3">
              Legal & Compliance
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-and-conditions" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/risk-disclaimer" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-amber-600 dark:text-amber-400 font-medium">
                  Financial Risk Disclaimer
                </Link>
              </li>
              <li>
                <span className="text-[11px] text-gray-500 flex items-center gap-1.5 pt-1">
                  <ShieldCheck size={13} className="text-emerald-500" /> Non-Custodial Architecture
                </span>
              </li>
            </ul>
          </div>

        </div>

        {/* Legal Disclaimer Box */}
        <div className="pt-6 border-t border-gray-200 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-500 leading-relaxed text-center sm:text-left">
          <p className="mb-2">
            <strong>Risk Warning:</strong> Trading cryptocurrency options and derivatives carries high level of risk and may not be suitable for all retail or institutional investors. Past performance of automated strategies or paper trading simulations does not indicate future results. AlgoMasters is a technology platform and does not provide financial or investment advice.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <span>© {currentYear} AlgoMasters. All rights reserved.</span>
            <span>Built with precision for quantitative derivative traders.</span>
          </div>
        </div>

      </div>
    </footer>
  );
}
